import React, {Component} from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import classNames from 'classnames';
import uuidV4 from 'uuid/v4';
import DOMUtils from '@spothero/utils/dom';
import withConfig from 'config/withConfig';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {push} from 'router/router-actions';
import {getPlaceUrl, setTerm} from 'search-request/search-request-actions';
import IconArrowLeft from '@spothero/icons/arrow-left';
import IconLocationArrow from '@spothero/icons/location-arrow';
import IconSearch from '@spothero/icons/search';
import Button from '@spothero/ui/button';
import Tab from '@spothero/ui/tab';
import TabContent from '@spothero/ui/tab-content';
import TabNavigation from '@spothero/ui/tab-navigation';
import Tabs from '@spothero/ui/tabs';
import PlaceSearch from 'search/controls/PlaceSearch';
import SearchGeocoderUtils from 'search/search-geocoder-utils';
import SelectAirport from 'search/controls/SelectAirport';

const DidYouMeanModal = loadable(() => import('./DidYouMeanModal'));
const HOURLY = 'Hourly';
const MONTHLY = 'Monthly';
const AIRPORT = 'Airport';
const TABS = [
    {
        term: HOURLY,
        placeholder: 'Address, Place, City or Venue',
    },
    {
        term: MONTHLY,
        placeholder: 'City, Address or Place',
    },
    {
        term: AIRPORT,
        placeholder: 'Airport Name, Code or City',
    },
];

class TabbedSearchControls extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        onSetTerm: PropTypes.func,
        pushTo: PropTypes.func,
        onGetPlaceUrl: PropTypes.func,
        isDisabled: PropTypes.bool,
        placeholder: PropTypes.string,
        selectedIndex: PropTypes.number,
        selectedTerm: PropTypes.string,
    };
    state = {
        address: '',
        placeModalData: null,
        resetKey: uuidV4(),
        showSearchModal: false,
    };

    componentDidMount() {
        this._searchControlsBody = DOMUtils.el('body');
    }

    componentWillUnmount() {
        DOMUtils.removeClass(this._searchControlsBody, 'no-bg-scroll');
    }

    _onHidePlaceModal = place => {
        this.setState({
            placeModalData: null,
        });

        if (place) {
            this._onPlaceSelect(place);
        }
    };

    _onShowPlaceModal = placeModalData => {
        this.setState({
            placeModalData,
        });
    };

    _onHideSearchModal = evt => {
        this.setState({
            showSearchModal: false,
        });

        DOMUtils.removeClass(this._searchControlsBody, 'no-bg-scroll');
    };

    _onShowSearchModal = evt => {
        this.setState({
            showSearchModal: true,
        });

        DOMUtils.addClass(this._searchControlsBody, 'no-bg-scroll');
    };

    _onUserInput = evt => {
        this.setState({
            address: evt.target.value,
        });
    };

    _onClearUserInput = () => {
        this.setState({
            address: '',
            resetKey: uuidV4(),
        });
    };

    _onTermSelect = selectedIndex => {
        const {onSetTerm} = this.props;
        const {term} = TABS[selectedIndex];

        this.setState({
            resetKey: uuidV4(),
        });

        onSetTerm({term});
    };

    _onAirportSelect = ({label}) => {
        this._matchPlace(label);
    };

    _onPlaceSelect = selectedPlace => {
        const {onGetPlaceUrl, pushTo} = this.props;

        onGetPlaceUrl(selectedPlace)
            .then(data => {
                pushTo(data.search_url, {cleanedParams: data});
            })
            .catch(() => this._onShowPlaceModal([]));
    };

    _onSubmit = evt => {
        evt.preventDefault();
        const {selectedTerm} = this.props;
        const {address} = this.state;

        if (selectedTerm === AIRPORT) {
            this._onAirportSelect({label: evt.target[AIRPORT].value});
        } else if (address) {
            this._matchPlace(address);
        } else {
            this._findParkingNearMe();
        }
    };

    _findParkingNearMe = () => {
        const {pushTo, selectedTerm} = this.props;
        const isMonthly = selectedTerm === MONTHLY;
        const searchUrl = `/${isMonthly ? 'monthly-' : ''}parking-near-me/`;

        pushTo(searchUrl);
    };

    _matchPlace = address => {
        return SearchGeocoderUtils.matchPlaces(address)
            .then(placeModalData => {
                return placeModalData.length === 1
                    ? this._onPlaceSelect(placeModalData[0])
                    : this._onShowPlaceModal(placeModalData);
            })
            .catch(this._onShowPlaceModal);
    };

    render() {
        const {
            isMobile,
            isDisabled,
            placeholder,
            selectedIndex,
            selectedTerm,
        } = this.props;
        const {address, placeModalData, resetKey, showSearchModal} = this.state;
        const formClasses = classNames(
            'TabbedSearchControls-form',
            {'TabbedSearchControls-modal': showSearchModal},
            `TabbedSearchControls-${selectedTerm.toLowerCase()}`
        );

        return (
            <section className="TabbedSearchControls">
                <form
                    action="."
                    className={formClasses}
                    id="TabbedSearchControls"
                    onSubmit={this._onSubmit}
                >
                    <header
                        className="TabbedSearchControls-modal-close"
                        onClick={this._onHideSearchModal}
                    >
                        <IconArrowLeft />
                    </header>
                    <Tabs
                        onSelect={this._onTermSelect}
                        selectedIndex={selectedIndex}
                    >
                        <TabNavigation>
                            {TABS.map(({term}) => (
                                <Tab label={term} key={term}>
                                    {term}
                                </Tab>
                            ))}
                        </TabNavigation>
                        <TabContent>{TABS.map(() => null)}</TabContent>
                    </Tabs>
                    <Button
                        className="TabbedSearchControls-modal-open"
                        iconPosition="left"
                        onClick={this._onShowSearchModal}
                    >
                        <IconSearch /> {placeholder}
                    </Button>
                    <div className="TabbedSearchControls-input">
                        {selectedTerm === AIRPORT && (
                            <SelectAirport
                                focus={
                                    isMobile
                                        ? selectedIndex === 2 && showSearchModal
                                        : selectedIndex === 2
                                }
                                autoFocus
                                key={`${resetKey}-airport`}
                                name={AIRPORT}
                                onChange={this._onUserInput}
                                onClear={this._onClearUserInput}
                                onSelect={this._onAirportSelect}
                                placeholder={placeholder}
                                preventScroll
                                searchText={address || ''}
                                type="search"
                            />
                        )}
                        <PlaceSearch
                            focus={
                                isMobile
                                    ? selectedIndex !== 2 && showSearchModal
                                    : selectedIndex !== 2
                            }
                            autoFocus
                            key={`${resetKey}-place`}
                            defaultValue={address}
                            onChange={this._onUserInput}
                            onClear={this._onClearUserInput}
                            onSelect={this._onPlaceSelect}
                            placeholder={placeholder}
                            preventScroll
                            type="search"
                        />
                        <Button
                            className="TabbedSearchControls-search"
                            color="primary"
                            disabled={isDisabled}
                            type="submit"
                        >
                            Search
                        </Button>
                    </div>
                    <Button
                        className="TabbedSearchControls-nearby"
                        color="primary"
                        disabled={isDisabled}
                        iconPosition="left"
                        onClick={this._onSubmit}
                    >
                        <IconLocationArrow /> Find Parking Near Me
                    </Button>
                </form>
                {placeModalData && (
                    <DidYouMeanModal
                        results={placeModalData}
                        onHidden={this._onHidePlaceModal}
                    />
                )}
            </section>
        );
    }
}

const mapStateToProps = (state, {tabs}) => {
    const {
        searchRequest: {
            airport,
            transient,
            monthly,
            isRouteLoading: isDisabled,
        },
    } = state;
    const selectedTerm =
        [transient && HOURLY, monthly && MONTHLY, airport && AIRPORT].find(
            Boolean
        ) || HOURLY;
    const selectedIndex = TABS.findIndex(({term}) => term === selectedTerm);

    return {
        isDisabled,
        selectedTerm,
        selectedIndex,
        placeholder: TABS[selectedIndex].placeholder,
    };
};

const mapDispatchToProps = {
    onSetTerm: setTerm,
    pushTo: push,
    onGetPlaceUrl: getPlaceUrl,
};

const enhance = compose(
    withConfig(['isMobile']),
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(TabbedSearchControls);
